import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { policiesService } from "../services/policiesService";
import type { AccessPolicy } from "../types/access.types";

/**
 * Loads the full policies catalog (`GET /internal/policies`). Used by the role editor to offer linkable
 * policies and (later) by the Policies section. Local state + service, like the other access hooks — settings
 * has no Redux slice.
 */
export function usePolicies() {
  const [policies, setPolicies] = useState<AccessPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoAccess, setHasNoAccess] = useState(false);

  // Monotonic load counter: concurrent `load()` calls (initial mount + a section's reload) can resolve out of
  // order, and the slower response must never overwrite the fresher one. Only the latest invocation may write
  // state. Bumped on unmount so an in-flight load can't set state on an unmounted hook.
  const loadSeq = useRef(0);
  useEffect(
    () => () => {
      loadSeq.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setIsLoading(true);
    setHasNoAccess(false);
    try {
      const data = await policiesService.listAll();
      if (seq !== loadSeq.current) return; // stale response — a newer load owns the state
      setPolicies(data);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if (HttpApiService.isAccessDeniedError(error)) {
        setHasNoAccess(true);
        setPolicies([]);
        return;
      }
      throw error;
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, []);

  // Memoized so the value forwarded by AccessDataProvider is stable across renders (consumers re-render only
  // on a real change). Adding a field here is the single place to touch — the provider just forwards this.
  return useMemo(
    () => ({ policies, isLoading, hasNoAccess, load }),
    [policies, isLoading, hasNoAccess, load],
  );
}
